import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MoneyField } from "@/features/admin/components";
import { EditorialBlock, type EditorialBlockLocaleProps } from "./EditorialBlock";
import { useBlockFilled } from "./useBlockFilled";
import type { CurrencyRowValues } from "../../productForm";

/**
 * Bloque 2 · El precio tachado que enseña la FICHA.
 *
 * No es el mismo que el `compareAtPrices` del paso de precios: aquel es
 * el del catálogo —el que sale en la tarjeta de la tienda— y este es el
 * de la pieza editorial, que puede contar otra historia ("antes, en
 * lanzamiento"). El contrato los tiene separados y aquí se respetan
 * separados; ponerlos en el mismo campo sería decidir por el negocio.
 *
 * Una fila por cada moneda ACTIVA del producto, las del paso 3: si allí
 * se añade una moneda, aquí aparece sola.
 */
export function EditorialCompareAtBlock({ locale }: EditorialBlockLocaleProps) {
  const { t } = useTranslation();
  const name = `editorial.${locale}.compareAt`;
  const currencies = (useWatch({ name: "currencies" }) ?? []) as CurrencyRowValues[];
  const filled = useBlockFilled(name);

  const codes = currencies
    .map((row) => row.code.trim().toUpperCase())
    .filter((code) => code !== "");

  return (
    <EditorialBlock
      name={name}
      title={t("Precio tachado de la ficha")}
      description={t("Lo que se enseña tachado en la ficha larga, moneda por moneda.")}
      filled={filled}
    >
      {codes.length === 0 ? (
        <p className="text-[13px] text-ds-ink-3 sm:col-span-2">
          {t("Añade una moneda en el paso de precios para poder escribir aquí.")}
        </p>
      ) : (
        codes.map((code) => (
          <MoneyField
            key={code}
            name={`${name}.${code}`}
            label={`${t("Precio tachado")} ${code}`}
            currency={code}
          />
        ))
      )}
    </EditorialBlock>
  );
}
